import { useState } from "react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { FileDown, FileSpreadsheet, FileText, Loader2 } from "lucide-react";

async function downloadBlob(url, fallbackName) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const match = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") || "");
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = match ? match[1] : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

const SCOPE_LABEL = { all: "all items", raw: "Raw Material only", spare: "Brought Out only", mfg_spare: "Mfg. Spares only", manufactured: "Manufactured only", archived: "Archived only" };

// Admin-only: download the catalog (items + recipes) as Excel or CSV, honouring the active BOM tab filter.
export function BomExportButton({ facility, scope = "all", count }) {
  const [busy, setBusy] = useState(false);
  const run = async (format) => {
    setBusy(true);
    try {
      await downloadBlob(`${API}/io/export/bom?format=${format}&scope=${scope}&facility=${encodeURIComponent(facility)}`, `bom-export.${format}`);
      toast.success(`BOM exported as ${format.toUpperCase()} — ${SCOPE_LABEL[scope] || scope}`);
    } catch (e) {
      logger.error("BOM export failed:", e);
      toast.error("Could not export the BOM");
    } finally {
      setBusy(false);
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy} data-testid="export-bom-btn">
          {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileDown className="h-3 w-3 mr-1" />} Export BOM
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="text-xs">
        <div className="px-2 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100 mb-1" data-testid="export-bom-scope">
          Exporting: <span className="text-slate-700 font-semibold normal-case">{SCOPE_LABEL[scope] || scope}</span>{typeof count === "number" ? ` · ${count} item${count !== 1 ? "s" : ""}` : ""}
        </div>
        <DropdownMenuItem onClick={() => run("xlsx")} data-testid="export-bom-xlsx"><FileSpreadsheet className="h-3.5 w-3.5 mr-2 text-emerald-600" /> Excel (.xlsx) — items + Recipes sheet</DropdownMenuItem>
        <DropdownMenuItem onClick={() => run("csv")} data-testid="export-bom-csv"><FileText className="h-3.5 w-3.5 mr-2 text-blue-600" /> CSV (.csv) — items with components column</DropdownMenuItem>
        {scope !== "all" && <p className="px-2 pt-1.5 pb-1 text-[10px] text-slate-400 border-t border-slate-100 mt-1">Switch the tab to “All” to export the whole catalog.</p>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
